/**
 * Session — reads/writes the `user_info` cookie set by the BFF.
 *
 * Auth contract (v1-aligned):
 * - The httpOnly `ando_session` cookie is OWNED by the backend; the SPA never
 *   touches it. It is sent automatically on every fetch with
 *   `credentials: "include"`.
 * - The non-httpOnly `user_info` cookie is set by the BFF
 *   (`apps/web/app/api/auth/idp-callback/route.ts`) and by `saveUserInfo()`
 *   after a password/TOTP login. It carries the user display payload so the
 *   SPA can render without an extra `/auth/me` round-trip.
 * - To "clear the session" client-side, call `clearSession()`. The httpOnly
 *   cookie can ONLY be cleared by the backend via `/auth/logout` Set-Cookie.
 *
 * NB Tauri: the Tauri webview runs on a different origin than the BFF. In that
 * setup the cookie is reachable only on the BFF host, so `getSession()` returns
 * `null` there and callers MUST fall back to `getCurrentUser()` (which hits
 * `/auth/me` with `credentials: "include"`). The session reader stays the same
 * for desktop and web — only the bootstrap path differs.
 */

import { getEnv } from "../env";
import type { UserInfo } from "./types";

const COOKIE_NAME = "user_info";

function getCookieDomain(): string {
  // Runtime config (window.__ANDO_RUNTIME__) is the cross-target source of
  // truth — Next.js injects it on the BFF response, Vite injects it via
  // /runtime-config.js. Either way the SPA reads the SAME field so we don't
  // need to know if we're in Next or Vite at compile time.
  const runtime = (globalThis as { __ANDO_RUNTIME__?: { cookieDomain?: string } }).__ANDO_RUNTIME__;
  return runtime?.cookieDomain || "";
}

/**
 * Minimal shape of the desktop token bundle the session reader needs.
 * Structurally compatible with ``DesktopTokenState`` in
 * ``apps/client/src/features/auth/desktop/tokenStore.ts`` — declared here
 * (not imported) so ``packages/core`` does NOT take a build-time dep on the
 * desktop feature module.
 */
export interface DesktopTokensLike {
  readonly access_token?: string | null;
  readonly user_info?: {
    readonly user_id?: string;
    readonly email?: string | null;
    readonly name?: string | null;
    readonly preferred_username?: string | null;
    readonly role?: string | null;
    readonly product_access?: UserInfo["product_access"];
  } | null;
}

/**
 * Desktop (Tauri) session source — registered by the app shell at boot.
 *
 * Why a registry and not a storage read: since the Sep 3, 2026 migration
 * from the Stronghold vault to the OS keychain (``KeyringStorage``) the
 * tokens live ONLY in the keychain, behind an async Tauri ``invoke``. The
 * previous reader probed a ``localStorage`` mirror that nothing writes any
 * more, so ``getSession()`` was always ``null`` on desktop and the
 * ``_authed`` guard bounced every login back to ``/login`` — for every
 * account. The token store already keeps an in-memory cache hydrated once
 * from the keychain; the guard awaits that hydrate (``hydrateSession``) and
 * every later ``getSession()`` reads the cache synchronously.
 */
export interface DesktopSessionSource {
  /** Synchronous read of the in-memory cache — ``null`` until hydrated. */
  peek(): DesktopTokensLike | null;
  /** One-shot keychain hydrate (idempotent, cached by the token store). */
  hydrate(): Promise<DesktopTokensLike | null>;
}

let desktopSessionSource: DesktopSessionSource | null = null;

/** Wire the desktop token store into the shared session reader (Tauri only). */
export function registerDesktopSessionSource(source: DesktopSessionSource | null): void {
  desktopSessionSource = source;
}

/**
 * Project a desktop token bundle into the ``UserInfo`` shape so the
 * ``_authed`` guard treats Tauri sessions uniformly with web ones.
 *
 * ``DesktopUserInfo`` (from the BE ``/auth/desktop/exchange`` projection)
 * carries ``user_id`` + ``email`` + ``name``/``preferred_username``;
 * ``org_id`` is empty until the next ``/auth/me`` round-trip — same
 * deal as the post-OTP-login cookie write path.
 */
export function desktopTokensToUserInfo(tokens: DesktopTokensLike | null): UserInfo | null {
  const info = tokens?.user_info;
  const userId = info?.user_id;
  if (!tokens?.access_token || !info || !userId) return null;
  return {
    user_id: userId,
    email: info.email ?? "",
    display_name: info.name ?? info.preferred_username ?? info.email ?? "",
    org_id: "",
    // ``role`` is carried in the desktop token projection (BE
    // ``/auth/desktop/exchange`` resolves it from ``users.role``). Falling
    // back to "user" was the bug that hid the Admin panel on the desktop
    // app for platform admins whose web session showed it.
    role: info.role ?? "user",
    ...(info.product_access ? { product_access: info.product_access } : {}),
  };
}

function readDesktopSession(): UserInfo | null {
  return desktopTokensToUserInfo(desktopSessionSource?.peek() ?? null);
}

/**
 * Async variant of ``getSession()`` for route guards: on desktop it awaits
 * the one-shot keychain hydrate before reading, so the first navigation
 * after a cold start (or right after the IdP exchange lands) sees the
 * session. On web it is exactly ``getSession()``.
 */
export async function hydrateSession(): Promise<UserInfo | null> {
  if (desktopSessionSource) {
    const tokens = await desktopSessionSource.hydrate();
    const fromVault = desktopTokensToUserInfo(tokens);
    if (fromVault) return fromVault;
  }
  return getSession();
}

/**
 * Parse the `user_info` cookie and return the user payload.
 * Returns `null` if the cookie is absent, malformed, or we're on the server.
 *
 * Tauri fallback: WebKit on macOS refuses to persist cookies on the
 * ``tauri://`` custom scheme (silent drop on every ``document.cookie =``
 * write). On that target the reader falls through to the registered
 * ``DesktopSessionSource`` (the token store's in-memory cache, hydrated
 * from the OS keychain — see ``hydrateSession``).
 */
export function getSession(): UserInfo | null {
  if (typeof document !== "undefined") {
    const cookies = document.cookie.split(";");
    const cookie = cookies.find((c) => c.trim().startsWith(`${COOKIE_NAME}=`));
    if (cookie) {
      try {
        const value = cookie.split("=").slice(1).join("=");
        return JSON.parse(decodeURIComponent(value)) as UserInfo;
      } catch (error) {
        console.error("[auth/session] Failed to parse user_info cookie:", error);
      }
    }
  }

  // No usable cookie — on Tauri the cookie is silently dropped by
  // WebKit, so fall through to the registered desktop session source.
  // On web (and on the server) this is ``null``: no source is registered.
  return readDesktopSession();
}

/**
 * Whether a session is a PLATFORM ADMIN. Mirrors the backend's
 * ``require_platform_admin`` semantics (``src/api/dependencies.py``): the boolean
 * ``is_platform_admin`` OR ``role in {"admin","superadmin"}``.
 *
 * The admin gate used to read ONLY the boolean, so a user whose ``role`` was set
 * to ``"admin"`` (but the flag left unset) was wrongly locked out — and changing
 * "role" in the admin UI appeared to do nothing. Single source of truth so every
 * gate / nav-visibility check stays consistent with the BE.
 */
/**
 * Returns ``true`` when the user can access an admin surface.
 *
 * Two scope flavours, mirror dei BE guards
 * (``ando-agent/src/api/dependencies.py``):
 *   * default (``scope: "tenant"``) — admin / superadmin entrano. Mirror
 *     di ``require_platform_admin``. Usato per surface single-tenant
 *     (Overview, Approval Queue, Facts Browser, …).
 *   * ``scope: "cross-tenant"`` — SOLO superadmin / ``is_platform_admin``.
 *     Mirror di ``require_true_platform_admin``. Usato per surface che
 *     espongono dati di ALTRI tenant (es. HyperMem "Per Tenant"). Un
 *     org-admin di Brini Legnami NON deve vedere i count di Smart-Lab
 *     o Rossi Forniture — GDPR data minimization + competitive intel.
 *
 * Tieni gli scope-string allineati col BE: se cambia un guard, aggiorni
 * il corrispondente check FE qui per evitare UX confondenti ("vedo la
 * tab ma mi ritorna 403 / 0 righe").
 */
export function isPlatformAdmin(
  session: UserInfo | null | undefined,
  scope: "tenant" | "cross-tenant" = "tenant",
): boolean {
  if (!session) return false;
  if (scope === "cross-tenant") {
    return session.is_platform_admin === true || session.role === "superadmin";
  }
  return (
    session.is_platform_admin === true || session.role === "admin" || session.role === "superadmin"
  );
}

/**
 * Persist user display data into the `user_info` cookie (client-side path).
 *
 * Used after a successful password/TOTP/OTP-email login. The cookie shape MUST
 * match the BFF server-side builder in `apps/web/app/lib/userInfoCookie.ts`
 * (the proxy revalidates against the same fields). `validated_at` is left
 * unset on the client path; the BFF stamps it on the next protected request.
 */
export function saveUserInfo(user: UserInfo): void {
  if (typeof document === "undefined") return;

  const payload = {
    user_id: user.user_id,
    email: user.email,
    display_name: user.display_name,
    org_id: user.org_id || "",
    // Optional pass-through — kept only when the login payload carries it.
    ...(user.org_name ? { org_name: user.org_name } : {}),
    role: user.role || "user",
    ...(user.product_access ? { product_access: user.product_access } : {}),
    is_platform_admin: user.is_platform_admin || false,
    is_active: user.is_active !== false,
    subscription_tier: user.subscription_tier || "free",
    subscription_status: user.subscription_status || "inactive",
    is_trialing: user.is_trialing || false,
  };

  const expires = new Date(Date.now() + 60 * 60 * 1000).toUTCString(); // 1h TTL — matches BFF middleware
  const cookieDomain = getCookieDomain();
  const domain = cookieDomain ? `; Domain=${cookieDomain}` : "";
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";

  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(payload))}; Path=/; Expires=${expires}; SameSite=Lax${secure}${domain}`;
}

/**
 * Clear the `user_info` cookie. The httpOnly `ando_session` is cleared by the
 * backend via the `/auth/logout` Set-Cookie response (see `logout()` in
 * `./login`).
 */
export function clearSession(): void {
  if (typeof document === "undefined") return;
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
  const cookieDomain = getCookieDomain();
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";

  // Match every variation the cookie may have been written with so we don't
  // leave a stale copy under a different Domain/Path attribute (e.g. local dev
  // host-only vs prod `.ando-ai.com`).
  if (cookieDomain) {
    document.cookie = `${COOKIE_NAME}=; Path=/; Domain=${cookieDomain}; Expires=${expired}; Max-Age=0; SameSite=Lax${secure}`;
  }
  document.cookie = `${COOKIE_NAME}=; Path=/; Expires=${expired}; Max-Age=0; SameSite=Lax${secure}`;
}

/**
 * Convenience: is the user authenticated according to the cookie snapshot?
 *
 * This is an OPTIMISTIC check — authoritative state lives in the httpOnly
 * `ando_session` cookie. The cookie can outlive the upstream session by up to
 * `USER_INFO_TTL_SECONDS`; on the next protected fetch the proxy will 401 and
 * `apiClient` should force a hard logout (see API client integration).
 */
export function isAuthenticated(): boolean {
  return getSession() !== null;
}

/** Build a fully-qualified URL against the BFF API base (e.g. http://localhost:3000). */
export function apiUrl(path: string): string {
  const base = getEnv().apiUrl.replace(/\/$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

/** Resolve a BE-supplied absolute path (already including ``/api/v1/...``)
 *  against the API host, NOT the page origin.
 *
 *  Use this for URLs the BE serializes inside JSON responses
 *  (``viewer_url``, ``preview_url``, ``download_url``, …) that point at
 *  full API paths. ``apiUrl()`` is wrong here because it appends the
 *  whole path to the configured ``apiUrl`` env — which in local dev
 *  is ``/api/v1`` (the Vite proxy match) and would produce a duplicated
 *  ``/api/v1/api/v1/...`` 404.
 *
 *  The contract:
 *    - apiUrl env is an ABSOLUTE URL (e.g. ``https://api.ando-ai.com``)
 *      → extract origin → concat the BE path verbatim.
 *    - apiUrl env is a RELATIVE path (``/api/v1`` in local dev)
 *      → return the BE path unchanged so the browser resolves it
 *        against the current origin (Vite dev server / nginx /api proxy).
 *
 *  Jun 18, 2026 — separates the two conventions so the prod cross-origin
 *  hop (``beta-web`` page → ``api.ando-ai.com`` proxy) and the local
 *  same-origin Vite proxy both work without per-env branches at the
 *  caller. Callers used to do ``apiUrl(file.viewer_url)`` which broke in
 *  local because of the duplicated ``/api/v1`` prefix.
 */
export function absoluteApiUrl(path: string): string {
  const env = getEnv().apiUrl;
  if (!env || !path) return path;
  // Absolute env (prod) — re-base onto its origin only.
  if (/^https?:\/\//.test(env)) {
    try {
      const origin = new URL(env).origin;
      return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
    } catch {
      return path;
    }
  }
  // Relative env (local dev) — leave the path alone; the page origin
  // already serves the API via the dev-server / nginx proxy.
  return path;
}
